import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { ArrowLeftIcon } from "./components/icons"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { Portal } from "./components/portal"
import { SplitActionButton } from "./components/split-action-button"
import { ActivityTimeline } from "./components/activity-timeline"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { ConfirmModal } from "./components/confirm-modal"
import { DetailPageSkeleton } from "./components/skeleton"
import { AdminActivityAction, AdminActivityEntityType } from "@shared/admin-activities"

type SchoolStatus = "application" | "active" | "inactive"
type SchoolType = "primary" | "secondary" | "combined"

interface School {
  id: string
  code: string
  name: string
  address: string | null
  city: string
  state: string
  lga: string | null
  enrollment: number
  type: SchoolType
  status: SchoolStatus
  contactName: string
  contactTitle: string | null
  contactPhone: string | null
  contactEmail: string | null
  since: number | null
  lastVisitAt: string | null
  nextVisitAt: string | null
  notes: string | null
  communicationLog: { event: string; date: string }[] | null
  childrenCount: number
  createdAt: string
  updatedAt: string
}

interface SchoolChild {
  id: string
  code: string
  firstName: string
  lastName: string
  classLevel: string
  status: string
  sponsorName: string | null
}

const STATUS_COLORS: Record<SchoolStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  application: { bg: "bg-gold-100", text: "text-gold-700" },
  inactive: { bg: "bg-red-100", text: "text-red-700" },
}

const TYPE_LABELS: Record<SchoolType, string> = {
  primary: "Primary School",
  secondary: "Secondary School",
  combined: "Combined School",
}

const DEFAULT_CHILD_LIMIT = 4

function formatDate(dateString: string | null): string {
  if (!dateString) return "—"
  const date = new Date(dateString)
  return date.toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatActivityDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "today"
  if (diffDays === 1) return "yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  return formatDate(dateString)
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function SchoolIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  )
}

export function SchoolDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("schools")
  const canMutate = useHasPermission("schools_mutate")
  const [school, setSchool] = useState<School | null>(null)
  const [children, setChildren] = useState<SchoolChild[]>([])
  const [availableChildren, setAvailableChildren] = useState<SchoolChild[]>([])
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [assigningChildren, setAssigningChildren] = useState(false)
  const [unassigningChildId, setUnassigningChildId] = useState<string | null>(null)
  const [loadingAvailableChildren, setLoadingAvailableChildren] = useState(false)
  const [childSearch, setChildSearch] = useState("")
  const [showEditModal, setShowEditModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showRecordModal, setShowRecordModal] = useState(false)
  const [showAssignChildrenModal, setShowAssignChildrenModal] = useState(false)
  const [activityRefreshKey, setActivityRefreshKey] = useState(0)
  const modalRef = useRef<HTMLDivElement>(null)

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    lga: "",
    enrollment: "",
    type: "primary" as SchoolType,
    contactName: "",
    contactTitle: "",
    contactPhone: "",
    contactEmail: "",
    notes: "",
  })

  // Message form state
  const [messageForm, setMessageForm] = useState({
    subject: "",
    message: "",
  })

  // Schedule form state
  const [visitDate, setVisitDate] = useState("")
  const [recordVisitDate, setRecordVisitDate] = useState("")
  const [recordVisitNotes, setRecordVisitNotes] = useState("")

  const fetchSchool = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.schools[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        const s = body.data as School
        setSchool(s)
        setEditForm({
          name: s.name,
          address: s.address ?? "",
          city: s.city,
          state: s.state,
          lga: s.lga ?? "",
          enrollment: String(s.enrollment),
          type: s.type,
          contactName: s.contactName,
          contactTitle: s.contactTitle ?? "",
          contactPhone: s.contactPhone ?? "",
          contactEmail: s.contactEmail ?? "",
          notes: s.notes ?? "",
        })
      }
    } catch {
      setError("Failed to load school")
    } finally {
      setLoading(false)
    }
  }

  const fetchChildren = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.schools[":id"].children.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setChildren(body.data as SchoolChild[])
      }
    } catch {
      // Silently fail - non-critical
    }
  }

  const fetchAvailableChildren = async () => {
    if (!id) return
    setLoadingAvailableChildren(true)
    try {
      const res = await client.api.admin.schools[":id"]["available-children"].$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) setAvailableChildren(body.data as SchoolChild[])
    } catch {
      toast.error("Failed to load available children")
    } finally {
      setLoadingAvailableChildren(false)
    }
  }

  const reloadPageData = async () => {
    await Promise.all([fetchSchool(), fetchChildren()])
    setActivityRefreshKey((key) => key + 1)
  }

  const openAssignChildrenModal = () => {
    setSelectedChildIds([])
    setChildSearch("")
    setAvailableChildren([])
    setShowAssignChildrenModal(true)
    void fetchAvailableChildren()
  }

  const handleAssignChildren = async () => {
    if (!id || !canMutate) return
    setAssigningChildren(true)
    try {
      const res = await client.api.admin.schools[":id"].children.$post({
        param: { id },
        json: { childIds: selectedChildIds },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) setChildren((current) => [...current, ...(body.data as SchoolChild[])])
      toast.success(`${selectedChildIds.length} child${selectedChildIds.length === 1 ? "" : "ren"} assigned to this school`)
      setSelectedChildIds([])
      setChildSearch("")
      setShowAssignChildrenModal(false)
      await reloadPageData()
    } catch {
      toast.error("Failed to assign children")
    } finally {
      setAssigningChildren(false)
    }
  }

  const handleUnassignChild = async () => {
    if (!id || !canMutate || !unassigningChildId) return
    const child = children.find((current) => current.id === unassigningChildId)
    if (!child) return
    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"].children[":childId"].$delete({
        param: { id, childId: child.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      setUnassigningChildId(null)
      toast.success(`${child.firstName} ${child.lastName} was unassigned from ${school?.name ?? "this school"}`)
      await reloadPageData()
    } catch {
      toast.error("Failed to unassign child")
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchSchool()
      void fetchChildren()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const allSelectableChildren = useMemo(() => {
    return [...availableChildren].sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
  }, [availableChildren])

  const selectableChildren = useMemo(() => {
    const search = childSearch.trim().toLowerCase()
    return allSelectableChildren.filter((child) => !search || [child.firstName, child.lastName, child.code, child.classLevel, child.status, child.sponsorName]
      .filter(Boolean)
      .some((field) => field!.toLowerCase().includes(search)))
  }, [allSelectableChildren, childSearch])

  const visibleChildren = useMemo(() => selectableChildren.slice(0, childSearch.trim() ? selectableChildren.length : DEFAULT_CHILD_LIMIT), [childSearch, selectableChildren])

  const handleApprove = async () => {
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"].approve.$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("School approved as active partner")
      await reloadPageData()
    } catch {
      toast.error("Failed to approve school")
    } finally {
      setSaving(false)
    }
  }

  const handleScheduleVisit = async () => {
    if (!id || !canMutate || !visitDate) return
    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"]["schedule-visit"].$post({
        param: { id },
        json: { visitDate: new Date(visitDate).toISOString() },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Visit scheduled successfully")
      setShowScheduleModal(false)
      setVisitDate("")
      await reloadPageData()
    } catch {
      toast.error("Failed to schedule visit")
    } finally {
      setSaving(false)
    }
  }

  const handleRecordVisit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!id || !canMutate || !recordVisitDate) return
    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"]["record-visit"].$post({
        param: { id },
        json: {
          visitDate: new Date(recordVisitDate).toISOString(),
          notes: recordVisitNotes.trim() || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Visit recorded successfully")
      setShowRecordModal(false)
      setRecordVisitDate("")
      setRecordVisitNotes("")
      await reloadPageData()
    } catch {
      toast.error("Failed to record visit")
    } finally {
      setSaving(false)
    }
  }

  const openMessageModal = () => {
    setMessageForm({
      subject: "",
      message: `Dear ${school?.contactName ?? "school partner"},\n\n`,
    })
    setShowMessageModal(true)
  }

  const handleMessageSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!id || !canMutate || !messageForm.subject.trim() || !messageForm.message.trim()) return

    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"].message.$post({
        param: { id },
        json: {
          subject: messageForm.subject.trim(),
          message: messageForm.message.trim(),
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Message sent to school contact")
      setShowMessageModal(false)
      setMessageForm({ subject: "", message: "" })
      await reloadPageData()
    } catch {
      toast.error("Failed to send message")
    } finally {
      setSaving(false)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.schools[":id"].$patch({
        param: { id },
        json: {
          name: editForm.name,
          address: editForm.address || undefined,
          city: editForm.city,
          state: editForm.state,
          lga: editForm.lga || undefined,
          enrollment: Number(editForm.enrollment),
          type: editForm.type,
          contactName: editForm.contactName,
          contactTitle: editForm.contactTitle || undefined,
          contactPhone: editForm.contactPhone || undefined,
          contactEmail: editForm.contactEmail || undefined,
          notes: editForm.notes || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("School updated successfully")
      setShowEditModal(false)
      await reloadPageData()
    } catch {
      toast.error("Failed to update school")
    } finally {
      setSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowEditModal(false)
      setShowMessageModal(false)
      setShowScheduleModal(false)
      setShowRecordModal(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">School</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this school.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !school) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/schools")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "School not found"}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/schools")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
              <SchoolIcon className="h-8 w-8" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/50">{school.code} · {school.city}, {school.state}</p>
              <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">{school.name}</h1>
              <p className="mt-1 text-sm text-ink/60">
                {TYPE_LABELS[school.type]} · {school.enrollment} students · {school.childrenCount} children in program
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                    STATUS_COLORS[school.status].bg
                  } ${STATUS_COLORS[school.status].text}`}
                >
                  {school.status === "application" ? "Pending Approval" : school.status}
                </span>
                {school.since && (
                  <span className="inline-flex items-center rounded-full bg-forest-100 px-2.5 py-0.5 text-xs font-medium text-forest-700">
                    Partner since {school.since}
                  </span>
                )}
              </div>
            </div>
          </div>

          {canMutate && (
            <SplitActionButton
              label="Message"
              icon={<MessageIcon className="h-4 w-4" />}
               onClick={openMessageModal}
              disabled={saving}
              items={[
                {
                  label: "Schedule visit",
                  icon: <CalendarIcon className="h-4 w-4" />,
                  onClick: () => setShowScheduleModal(true),
                },
                {
                  label: "Record visit",
                  icon: <CheckIcon className="h-4 w-4" />,
                  onClick: () => setShowRecordModal(true),
                },
                ...(school.status === "application"
                  ? [{ label: "Approve", icon: <CheckIcon className="h-4 w-4" />, onClick: () => void handleApprove() }]
                  : []),
                {
                  label: "Edit",
                  icon: <EditIcon className="h-4 w-4" />,
                  onClick: () => setShowEditModal(true),
                },
              ]}
            />
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-5 w-5 text-forest-700" />
            <span className="text-xs uppercase tracking-wider text-ink/50">Our Children</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-ink">{school.childrenCount}</p>
        </div>
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <div className="flex items-center gap-3">
            <SchoolIcon className="h-5 w-5 text-forest-700" />
            <span className="text-xs uppercase tracking-wider text-ink/50">Enrollment</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-ink">{school.enrollment}</p>
        </div>
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-5 w-5 text-forest-700" />
            <span className="text-xs uppercase tracking-wider text-ink/50">Last Visit</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-ink">{formatDate(school.lastVisitAt)}</p>
        </div>
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-5 w-5 text-forest-700" />
            <span className="text-xs uppercase tracking-wider text-ink/50">Next Visit</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-ink">{formatDate(school.nextVisitAt)}</p>
        </div>
      </div>

      {/* School Profile */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">School Profile</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-ink/50">School Name</p>
            <p className="text-sm font-medium text-ink">{school.name}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">School Code</p>
            <p className="text-sm font-medium text-ink">{school.code}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Type</p>
            <p className="text-sm font-medium text-ink">{TYPE_LABELS[school.type]}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Address</p>
            <p className="text-sm font-medium text-ink">{school.address || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">City</p>
            <p className="text-sm font-medium text-ink">{school.city}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">State</p>
            <p className="text-sm font-medium text-ink">{school.state}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">LGA</p>
            <p className="text-sm font-medium text-ink">{school.lga || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Partner Since</p>
            <p className="text-sm font-medium text-ink">{school.since ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Status</p>
            <p className="text-sm font-medium text-ink capitalize">{school.status}</p>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Contact</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-ink/50">Contact Name</p>
            <p className="text-sm font-medium text-ink">{school.contactName}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Title</p>
            <p className="text-sm font-medium text-ink">{school.contactTitle || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Phone</p>
            <p className="text-sm font-medium text-ink">{school.contactPhone || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Email</p>
            <p className="text-sm font-medium text-ink">{school.contactEmail || "—"}</p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Notes</h2>
        <div className="mt-4 rounded-lg bg-cream-100 p-3">
          <p className="text-sm text-ink">{school.notes || "No notes."}</p>
        </div>
      </div>

      {/* Children at School */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">
            Our Children at This School
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-ink/50">{children.length} children</span>
            {canMutate && (
              <button
                type="button"
                onClick={openAssignChildrenModal}
                className="rounded-full bg-forest-950 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-forest-900"
              >
                Assign children
              </button>
            )}
          </div>
        </div>
        {children.length > 0 ? (
          <div className="mt-4 space-y-3">
            {children.map((child) => (
              <div
                key={child.id}
                onClick={() => navigate(`/admin/children/${child.id}`)}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-gold-500/30 p-3 transition-colors hover:bg-cream-50"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                    {child.firstName[0]}{child.lastName[0]}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-ink">{child.firstName} {child.lastName}</p>
                    <p className="text-xs text-ink/50">
                      {child.code} · {child.classLevel} · {child.sponsorName ? `Sponsored by ${child.sponsorName}` : "Unsponsored"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {canMutate && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        setUnassigningChildId(child.id)
                      }}
                      className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      Unassign
                    </button>
                  )}
                  <ExternalLinkIcon className="h-4 w-4 text-ink/40" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-lg bg-cream-100 p-4 text-center">
            <p className="text-sm text-ink/70">No children enrolled from this school yet.</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showAssignChildrenModal}
        onClose={() => {
          if (!assigningChildren) setShowAssignChildrenModal(false)
        }}
        title="Assign children"
        subtitle={`Select unassigned children to add to ${school.name}.`}
        maxWidth="md"
      >
        {loadingAvailableChildren ? (
          <p className="rounded-lg bg-cream-100 p-4 text-sm text-ink/60">Loading children...</p>
        ) : allSelectableChildren.length === 0 ? (
          <p className="rounded-lg bg-cream-100 p-4 text-sm text-ink/60">All children are already assigned to a school.</p>
        ) : (
          <div className="space-y-3">
            <div className="sticky top-0 z-10 bg-white">
              <label className="sr-only" htmlFor="school-child-search">Search children</label>
              <input
                id="school-child-search"
                type="search"
                value={childSearch}
                onChange={(event) => setChildSearch(event.target.value)}
                placeholder="Search by name, code, class, or status..."
                className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between text-xs text-ink/50">
                <span>{selectedChildIds.length} selected</span>
                <span>
                  {childSearch.trim()
                    ? `${visibleChildren.length} match${visibleChildren.length === 1 ? "" : "es"}`
                    : `Showing ${visibleChildren.length} of ${selectableChildren.length}`}
                </span>
              </div>
            </div>
            <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {selectableChildren.length === 0 ? (
              <p className="rounded-lg bg-cream-100 p-4 text-sm text-ink/60">No children match your search.</p>
            ) : visibleChildren.map((child) => {
              const selected = selectedChildIds.includes(child.id)
              return (
                <label key={child.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${selected ? "border-forest-700 bg-forest-50" : "border-gold-500/40 hover:bg-cream-50"}`}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => setSelectedChildIds((current) => selected ? current.filter((childId) => childId !== child.id) : [...current, child.id])}
                    className="h-4 w-4 rounded border-gold-500 text-forest-700"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink">{child.firstName} {child.lastName}</span>
                    <span className="block text-xs text-ink/50">{child.code} · {child.classLevel} · {child.status}</span>
                  </span>
                </label>
              )
            })}
            </div>
            {!childSearch.trim() && selectableChildren.length > visibleChildren.length && (
              <p className="text-xs text-ink/50">Search to find more children.</p>
            )}
          </div>
        )}
        <ModalActions>
          <ModalButton onClick={() => setShowAssignChildrenModal(false)} disabled={assigningChildren}>Cancel</ModalButton>
          <ModalButton onClick={() => void handleAssignChildren()} variant="primary" disabled={assigningChildren || loadingAvailableChildren || selectedChildIds.length === 0}>
            {assigningChildren ? "Assigning..." : "Assign selected"}
          </ModalButton>
        </ModalActions>
      </Modal>

      <ConfirmModal
        open={unassigningChildId !== null}
        title="Unassign child?"
        message={`Remove ${children.find((child) => child.id === unassigningChildId)?.firstName ?? "this child"} from ${school.name}?`}
        confirmLabel={saving ? "Unassigning..." : "Unassign child"}
        onCancel={() => {
          if (!saving) setUnassigningChildId(null)
        }}
        onConfirm={() => void handleUnassignChild()}
      />

      <Modal
        isOpen={showRecordModal}
        onClose={() => {
          if (!saving) setShowRecordModal(false)
        }}
        title="Record Visit"
        subtitle={`${school.code} · ${school.name}`}
        maxWidth="md"
      >
        <form onSubmit={handleRecordVisit} className="space-y-4">
          <DatePicker
            label="Visit date *"
            value={recordVisitDate}
            onChange={setRecordVisitDate}
            max={new Date()}
          />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink">Visit notes</span>
            <textarea
              value={recordVisitNotes}
              onChange={(event) => setRecordVisitNotes(event.target.value)}
              rows={4}
              maxLength={5000}
              placeholder="Add notes about the visit"
              className="w-full rounded-lg border border-gold-500/60 bg-white px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700"
            />
          </label>
          <ModalActions>
            <ModalButton
              onClick={() => setShowRecordModal(false)}
              disabled={saving}
            >
              Cancel
            </ModalButton>
            <ModalButton
              type="submit"
              variant="primary"
              disabled={saving || !recordVisitDate}
            >
              {saving ? "Recording..." : "Record Visit"}
            </ModalButton>
          </ModalActions>
        </form>
      </Modal>

      <ActivityTimeline
        entityType={AdminActivityEntityType.School}
        entityId={school.id}
        title="Communication"
        action={AdminActivityAction.SchoolMessageSent}
        refreshKey={activityRefreshKey}
        fallback={
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Communication</h2>
            {school.communicationLog && school.communicationLog.length > 0 ? (
              <div className="mt-4 space-y-3">
                {school.communicationLog.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-forest-700" />
                    <div>
                      <span className="text-sm font-medium text-ink">{item.event}</span>
                      <span className="text-sm text-ink/50"> · {formatActivityDate(item.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-ink/50">No communication recorded.</p>
            )}
          </div>
        }
      />

      <ActivityTimeline
        entityType={AdminActivityEntityType.School}
        entityId={school.id}
        title="Activity"
        excludeActions={[AdminActivityAction.SchoolMessageSent]}
        hideWhenEmpty
        refreshKey={activityRefreshKey}
      />

      {/* Edit Modal */}
      {showEditModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Edit School</h2>
            <p className="mt-1 text-sm text-ink/60">{school.code} · {school.name}</p>
            <form onSubmit={handleEditSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">School Name *</span>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Address</span>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">City *</span>
                  <input
                    type="text"
                    required
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <Select
                  label="State *"
                  value={editForm.state}
                  onChange={(value) => setEditForm({ ...editForm, state: value })}
                  options={NIGERIAN_STATES}
                  placeholder="Select state"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">LGA</span>
                  <input
                    type="text"
                    value={editForm.lga}
                    onChange={(e) => setEditForm({ ...editForm, lga: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Enrollment</span>
                  <input
                    type="number"
                    value={editForm.enrollment}
                    onChange={(e) => setEditForm({ ...editForm, enrollment: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <Select
                label="School Type"
                value={editForm.type === "primary" ? "Primary School" : editForm.type === "secondary" ? "Secondary School" : "Combined School"}
                onChange={(label) => {
                  const typeMap: Record<string, SchoolType> = {
                    "Primary School": "primary",
                    "Secondary School": "secondary",
                    "Combined School": "combined",
                  }
                  setEditForm({ ...editForm, type: typeMap[label] || "primary" })
                }}
                options={["Primary School", "Secondary School", "Combined School"]}
              />

              <div className="border-t border-ink/10 pt-4">
                <h3 className="text-sm font-medium text-ink">Contact Information</h3>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Contact Name *</span>
                  <input
                    type="text"
                    required
                    value={editForm.contactName}
                    onChange={(e) => setEditForm({ ...editForm, contactName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Title</span>
                  <input
                    type="text"
                    value={editForm.contactTitle}
                    onChange={(e) => setEditForm({ ...editForm, contactTitle: e.target.value })}
                    placeholder="e.g. Principal"
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Phone</span>
                  <input
                    type="tel"
                    value={editForm.contactPhone}
                    onChange={(e) => setEditForm({ ...editForm, contactPhone: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Email</span>
                  <input
                    type="email"
                    value={editForm.contactEmail}
                    onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Notes</span>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Internal notes about this school..."
                />
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Message School</h2>
            <p className="mt-1 text-sm text-ink/60">{school.code} · {school.contactName}</p>
            <form className="mt-6 space-y-4" onSubmit={handleMessageSubmit}>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Subject</span>
                <input
                  type="text"
                  required
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Your message subject"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Message</span>
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  rows={5}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder={`Dear ${school.contactName},\n\nWe wanted to reach out regarding...`}
                />
              </label>

              <div className="flex justify-start gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving || !messageForm.subject.trim() || !messageForm.message.trim()}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Sending..." : "Send message"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Schedule Visit Modal */}
      {showScheduleModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Schedule Visit</h2>
            <p className="mt-1 text-sm text-ink/60">{school.code} · {school.name}</p>
            <div className="mt-6 space-y-4">
              <DatePicker
                label="Visit Date *"
                value={visitDate}
                onChange={setVisitDate}
                min={new Date()}
              />

              <div className="rounded-lg border border-gold-500 bg-gold-50 p-3">
                <p className="text-sm text-gold-700">
                  The school contact will be notified about the scheduled visit.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowScheduleModal(false)
                    setVisitDate("")
                  }}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleScheduleVisit}
                  disabled={saving || !visitDate}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Scheduling..." : "Schedule Visit"}
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
